import React from 'react';
import {useIntl} from '@sail/intl';
import {
  HeroSection as HelmHeroSection,
  ColumnLayout,
  Copy,
  CtaButton,
  StripeSet,
} from '@sail/helm';
import {assignProps} from '@sail/engine';
import slugify from 'slugify';
import {useLinkResolver} from 'mkt-ssr/lib/routing/LinkResolver';
import {BenefitsList} from 'mkt-ssr/components/sail/pages/StartupPartners/BenefitsList';
import * as messages from 'mkt-ssr/components/sail/pages/StartupPartners/HeroSection.messages';
import {
  StartupPartnersBenefitsPackageFields,
  StartupPartnersPageCurrency,
} from '@stripe-internal/mkt-ssr-models';

import {css} from '@sail/ui';

interface HeroSectionProps {
  partnerName: string;
  partnerCode: string;
  benefitsTier: StartupPartnersBenefitsPackageFields;
  waivedProcessingFee?: number;
  currency?: StartupPartnersPageCurrency;
  customBenefits?: string[];
  waivedProcessingAmount?: number;
  creditAmount?: number;
}

export const HeroSection: React.FC<
  React.PropsWithChildren<HeroSectionProps>
> = ({
  partnerName,
  partnerCode,
  benefitsTier,
  waivedProcessingFee,
  currency,
  customBenefits,
  waivedProcessingAmount,
  creditAmount,
}) => {
  const intl = useIntl();
  const {createRoute} = useLinkResolver();

  const hasCreditAndProcessingAmount =
    waivedProcessingAmount !== undefined && creditAmount !== undefined;

  return (
    <HelmHeroSection
      angle="bottom"
      contentAngledMarginBottom="none"
      uses={[
        css({
          '--sectionAnglePaddingBaseMax': '24',
        }),
      ]}
    >
      <ColumnLayout columnsTablet="1">
        <Copy
          css={{paddingBottom: '24px'}}
          variant="hero"
          caption={
            hasCreditAndProcessingAmount ? (
              <svg
                width="127"
                height="30"
                viewBox="0 0 127 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="m20.3626 17.5057 1.121-.9265c.572.8922 1.4299 1.407 2.3679 1.407.9266 0 1.6243-.4233 1.6243-1.2126 0-.835-.8579-.9494-2.0819-1.2468-1.2354-.2974-2.5623-.6635-2.5623-2.2649 0-1.5214 1.304-2.4938 2.9627-2.4938 1.407 0 2.5051.6635 3.0542 1.5214l-1.0753.9266c-.4575-.7092-1.0638-1.2011-2.0247-1.2011-.8808 0-1.487.4461-1.487 1.0981 0 .7207.6291.8694 1.7387 1.1211 1.3269.2974 2.917.6062 2.917 2.3907 0 1.6129-1.4185 2.6081-3.1115 2.6081-1.407 0-2.8025-.6749-3.4431-1.7273ZM34.6064 12.1751H31.964v6.9321h-1.4642v-6.9321h-2.6424V10.894h6.749v1.2811ZM37.3646 15.9271v3.1801h-1.4642V10.894h3.283c1.8302 0 2.9741.8579 2.9741 2.5166 0 1.2926-.7092 2.1162-1.9103 2.4022l2.1162 3.2944h-1.6701l-2.0018-3.1801h-1.3269Zm0-1.2582h1.7387c1.0524 0 1.6129-.4576 1.6129-1.2583 0-.8122-.5605-1.2583-1.6129-1.2583h-1.7387v2.5166ZM45.5242 10.894v8.2132H44.06V10.894h1.4642ZM49.2267 15.9271v3.1801h-1.4642V10.894h3.283c1.8303 0 2.9742.8579 2.9742 2.5166 0 1.6357-1.1439 2.5165-2.9742 2.5165h-1.8188Zm0-1.2582h1.7388c1.0524 0 1.6129-.4576 1.6129-1.2583 0-.8122-.5605-1.2583-1.6129-1.2583h-1.7388v2.5166ZM55.4477 10.894h5.5593v1.2811h-4.118v2.0591h3.466v1.2811h-3.466v2.3107h4.118v1.2812h-5.5593V10.894ZM65.5919 17.5057l1.121-.9265c.5719.8922 1.4299 1.407 2.3679 1.407.9265 0 1.6243-.4233 1.6243-1.2126 0-.835-.8579-.9494-2.0819-1.2468-1.2354-.2974-2.5623-.6635-2.5623-2.2649 0-1.5214 1.304-2.4938 2.9627-2.4938 1.407 0 2.5051.6635 3.0542 1.5214l-1.0753.9266c-.4575-.7092-1.0638-1.2011-2.0247-1.2011-.8808 0-1.4871.4461-1.4871 1.0981 0 .7207.6292.8694 1.7388 1.1211 1.3269.2974 2.9169.6062 2.9169 2.3907 0 1.6129-1.4184 2.6081-3.1114 2.6081-1.407 0-2.8025-.6749-3.4431-1.7273ZM79.8357 12.1751h-2.6424v6.9321h-1.4642v-6.9321h-2.6424V10.894h6.749v1.2811ZM82.8345 10.894h1.6587l3.1114 8.2132h-1.4757l-.7092-1.876h-3.5461l-.6978 1.876h-1.4527l3.1114-8.2132Zm.8007 1.6243L82.3541 15.95h2.5852l-1.3041-3.4317ZM90.5328 15.9271v3.1801h-1.4642V10.894h3.283c1.8302 0 2.9741.8579 2.9741 2.5166 0 1.2926-.7092 2.1162-1.9103 2.4022l2.1162 3.2944h-1.6701l-2.0018-3.1801h-1.3269Zm0-1.2582h1.7387c1.0524 0 1.6129-.4576 1.6129-1.2583 0-.8122-.5605-1.2583-1.6129-1.2583h-1.7387v2.5166ZM103.145 12.1751h-2.642v6.9321h-1.4647v-6.9321h-2.6424V10.894h6.7491v1.2811ZM107.733 19.2216c-1.956 0-3.306-1.1096-3.306-3.3745V10.894h1.465v4.9073c0 1.4985.743 2.1277 1.841 2.1277 1.11 0 1.853-.6292 1.853-2.1277V10.894h1.465v4.9531c0 2.2649-1.35 3.3745-3.318 3.3745ZM114.406 15.9271v3.1801h-1.464V10.894h3.283c1.83 0 2.974.8579 2.974 2.5166 0 1.6357-1.144 2.5165-2.974 2.5165h-1.819Zm0-1.2582h1.739c1.053 0 1.613-.4576 1.613-1.2583 0-.8122-.56-1.2583-1.613-1.2583h-1.739v2.5166ZM120.133 17.5057l1.121-.9265c.572.8922 1.43 1.407 2.368 1.407.926 0 1.624-.4233 1.624-1.2126 0-.835-.858-.9494-2.082-1.2468-1.235-.2974-2.562-.6635-2.562-2.2649 0-1.5214 1.304-2.4938 2.962-2.4938 1.407 0 2.506.6635 3.055 1.5214l-1.076.9266c-.457-.7092-1.063-1.2011-2.024-1.2011-.881 0-1.487.4461-1.487 1.0981 0 .7207.629.8694 1.738 1.1211 1.327.2974 2.917.6062 2.917 2.3907 0 1.6129-1.418 2.6081-3.111 2.6081-1.407 0-2.803-.6749-3.443-1.7273Z"
                  fill="#031323"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="m0 22 14-2.9416V8L0 10.8872V22Z"
                  fill="#031323"
                />
              </svg>
            ) : (
              intl.formatMessage(messages.sectionCaption)
            )
          }
          title={intl.formatMessage(messages.sectionTitle, {partnerName})}
          body={intl.formatMessage(messages.sectionBody)}
          cta={
            <CtaButton
              href={createRoute(
                `https://dashboard.stripe.com/register/startup?partner_code=${partnerCode}&partner_page=${slugify(
                  partnerName.toLowerCase(),
                )}`,
              )}
            >
              {intl.formatMessage(messages.ctaButtonText)}
            </CtaButton>
          }
          subviews={{
            header: assignProps({
              columnPaddingRight: 'large',
            }),
            title: {
              uses: [
                css({
                  textWrap: 'balance',
                }),
              ],
            },
          }}
        />
        <BenefitsList
          benefitsTier={benefitsTier}
          waivedProcessingFee={waivedProcessingFee}
          currency={currency}
          customBenefits={customBenefits}
          waivedProcessingAmount={waivedProcessingAmount}
          creditAmount={creditAmount}
        />
        <StripeSet
          position="bottom-left"
          stripes={[{width: 'full', inset: 'small'}, {width: 'full'}]}
        />
      </ColumnLayout>
    </HelmHeroSection>
  );
};
